import React, { useCallback, useEffect, useState } from 'react';
import { Users, Sparkles, RefreshCw, Check, Rocket, Scale, Gauge, Target, Brain } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanCard = ({ plan, label, chosen, recommended, onChoose, disabled, testId }) => (
  <div className={`rounded-2xl border p-4 transition ${chosen ? 'border-primary/60 bg-primary/5' : 'border-border-subtle bg-background/50'}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${label === 'A' ? 'bg-emerald-500 text-background' : 'bg-sky-500 text-background'}`}>{label}</span>
        <h5 className="text-sm font-semibold text-text-heading">{plan?.title || `Plan ${label}`}</h5>
      </div>
      {recommended && <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-200">Recomendado</span>}
    </div>
    <p className="mt-2 text-xs text-text-muted">{plan?.summary}</p>
    <ol className="mt-3 list-decimal space-y-1 pl-5 text-[11.5px] text-text-muted">
      {(plan?.steps || []).map((s, i) => <li key={i}>{s}</li>)}
    </ol>
    <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-text-muted">
      <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5"><Gauge className="h-3 w-3" /> Esfuerzo: {plan?.effort}</span>
      <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5"><Target className="h-3 w-3" /> Impacto: {plan?.impact}</span>
      <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5">⏱ {plan?.horizon}</span>
    </div>
    {plan?.why && <p className="mt-2 text-[11px] italic text-text-muted/80">{plan.why}</p>}
    <button type="button" disabled={disabled} onClick={onChoose} data-testid={`${testId}-choose`}
      className={`mt-3 inline-flex w-full items-center justify-center gap-1 rounded-full px-4 py-2 text-xs font-semibold transition disabled:opacity-40 ${chosen ? 'bg-primary text-background' : 'border border-border-subtle text-text-muted hover:text-text-heading'}`}>
      <Check className="h-3.5 w-3.5" /> {chosen ? `Plan ${label} elegido` : `Elegir Plan ${label}`}
    </button>
  </div>
);

export const MentorsCouncilPanel = ({ authFetch }) => {
  const [status, setStatus] = useState(null);
  const [council, setCouncil] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [pending, setPending] = useState([]);
  const [clientId, setClientId] = useState('neuralia369');
  const [topic, setTopic] = useState('');
  const [selectedMentors, setSelectedMentors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [advising, setAdvising] = useState(false);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (notify = false) => {
    try {
      setLoading(true);
      const [s, se, cl] = await Promise.all([authFetch(`${API}/mentors/status`), authFetch(`${API}/mentors/sessions`), authFetch(`${API}/mentors/clients`)]);
      const sj = await s.json(), sej = await se.json(), clj = await cl.json();
      setStatus(sj); setCouncil(sj.council || []); setSessions(sej.sessions || []); setClients(clj.clients || []);
      try {
        const pd = await authFetch(`${API}/mentors/pending-decisions`);
        const pdj = await pd.json();
        setPending(pdj.pending || []);
      } catch (e) { /* noop */ }
      if (notify) toast.success('Consejo actualizado');
    } catch (e) { toast.error('No se pudo cargar el Consejo de Mentores'); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggleMentor = (id) => setSelectedMentors((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const advise = async () => {
    if (topic.trim().length < 3) { toast.error('Describe el tema a consultar'); return; }
    try {
      setAdvising(true); setActive(null);
      const r = await authFetch(`${API}/mentors/advise`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId.trim() || 'neuralia369', topic: topic.trim(), mentor_ids: selectedMentors }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error del consejo');
      toast.success(j.simulated ? 'Dictamen generado (heurístico, flag OFF)' : 'Dictamen del consejo con IA');
      setActive(j); await load();
    } catch (e) { toast.error(e.message); } finally { setAdvising(false); }
  };

  const decide = async (plan) => {
    if (!active) return;
    try {
      setBusy(true);
      const r = await authFetch(`${API}/mentors/sessions/${active.session_id}/decide`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chosen_plan: plan, approved_by: 'admin', note: 'Decidido desde el panel' }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error');
      toast.success(`Plan ${plan} aprobado`);
      setActive({ ...active, status: 'decided', chosen_plan: plan }); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openSession = async (id) => {
    try {
      const r = await authFetch(`${API}/mentors/sessions/${id}`);
      const j = await r.json();
      if (r.ok) setActive(j);
    } catch (e) { /* noop */ }
  };

  const st = status?.stats || {};
  const adv = active?.advice;

  return (
    <section className="space-y-6" data-testid="mentors-council-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">Fase 10 · Consejo de Mentores IA</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Consejo de Mentores · Plan A / Plan B</h3>
          <p className="mt-1 text-xs text-text-muted">Un consejo de mentores IA analiza a cada cliente y entrega dos rutas con aprobación humana.</p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} data-testid="mentors-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {pending.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4" data-testid="mentors-pending-banner">
          <Scale className="mt-0.5 h-5 w-5 text-amber-300" />
          <div>
            <p className="text-sm font-semibold text-amber-100">{pending.length} dictamen(es) esperan tu decisión hace +2 días</p>
            <p className="mt-0.5 text-xs text-amber-100/70">{pending.map((p) => `${p.client_id} · ${p.topic}${p.days_waiting ? ` (${p.days_waiting}d)` : ''}`).slice(0, 3).join(' — ')}</p>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { icon: Brain, label: 'Dictámenes', value: st.sessions_total ?? 0, helper: 'generados', id: 'mentors-stat-total' },
          { icon: Scale, label: 'Pendientes', value: st.pending ?? 0, helper: 'sin decisión', id: 'mentors-stat-pending' },
          { icon: Check, label: 'Decididos', value: st.decided ?? 0, helper: 'con Plan elegido', id: 'mentors-stat-decided' },
        ].map((c) => (
          <div key={c.id} className="glass-card p-5" data-testid={c.id}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{c.label}</p>
                <p className="mt-3 text-3xl font-bold text-text-heading">{c.value}</p>
                <p className="mt-2 text-xs text-text-muted">{c.helper}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><c.icon className="h-5 w-5" /></div>
            </div>
          </div>
        ))}
      </div>

      {/* El consejo */}
      <div className="glass-card p-5" data-testid="mentors-council-roster">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Users className="h-4 w-4 text-primary" /> El consejo</h4>
        <div className="flex flex-wrap gap-2">
          {council.map((m) => (
            <button key={m.id} type="button" onClick={() => toggleMentor(m.id)} data-testid={`mentor-chip-${m.id}`}
              className={`rounded-full border px-3 py-1.5 text-xs transition ${selectedMentors.includes(m.id) ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border-subtle text-text-muted hover:text-text-heading'}`}
              title={m.focus}>
              <span className="font-semibold">{m.name}</span> · {m.role}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-text-muted">Sin selección = consulta a todo el consejo.</p>
      </div>

      {/* Consultar */}
      <div className="glass-card p-5" data-testid="mentors-consult">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Sparkles className="h-4 w-4 text-primary" /> Consultar al consejo</h4>
        <div className="grid gap-3 md:grid-cols-[220px_1fr_auto]">
          <select data-testid="mentors-client-input" value={clientId} onChange={(e) => setClientId(e.target.value)}
            className="rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none">
            {!clients.some((c) => c.client_id === clientId) && <option value={clientId}>{clientId}</option>}
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.client_id}{c.active_plan ? ` · Plan ${c.active_plan.chosen_plan} ✓` : ''}
              </option>
            ))}
          </select>
          <input data-testid="mentors-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
            className="rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none" placeholder="Tema: ej. aumentar ventas / reducir costos de IA" />
          <button type="button" onClick={advise} disabled={advising} data-testid="mentors-advise-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-semibold text-background transition hover:opacity-90 disabled:opacity-50">
            {advising ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Pedir dictamen
          </button>
        </div>
        {(() => {
          const sel = clients.find((c) => c.client_id === clientId);
          if (sel?.active_plan) {
            return (
              <div className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-xs text-emerald-100" data-testid="mentors-client-active-plan">
                <span className="font-semibold">Plan vigente en su ficha:</span> Plan {sel.active_plan.chosen_plan} — {sel.active_plan.plan?.title || sel.active_plan.topic}
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Dictamen activo */}
      {active && adv && (
        <div className="glass-card space-y-4 p-5" data-testid="mentors-advice">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{active.client_id} · {active.source}{active.simulated ? ' (simulado)' : ''}</p>
              <h4 className="mt-1 text-base font-semibold text-text-heading">{active.topic}</h4>
            </div>
            {active.status === 'decided' && <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[11px] text-emerald-200">Plan {active.chosen_plan} aprobado</span>}
          </div>
          <p className="rounded-xl border border-border-subtle bg-background/50 p-3 text-xs text-text-muted" data-testid="mentors-diagnosis">{adv.diagnosis}</p>
          <div className="grid gap-4 md:grid-cols-2">
            <PlanCard plan={adv.plan_a} label="A" recommended={adv.recommendation === 'A'} chosen={active.chosen_plan === 'A'} disabled={busy} onChoose={() => decide('A')} testId="mentors-plan-a" />
            <PlanCard plan={adv.plan_b} label="B" recommended={adv.recommendation === 'B'} chosen={active.chosen_plan === 'B'} disabled={busy} onChoose={() => decide('B')} testId="mentors-plan-b" />
          </div>
          {!!(adv.kpis || []).length && (
            <p className="text-[11px] text-text-muted">KPIs a vigilar: {adv.kpis.join(' · ')}</p>
          )}
        </div>
      )}

      {/* Historial */}
      <div className="glass-card p-5" data-testid="mentors-sessions-card">
        <h4 className="mb-4 text-sm font-semibold text-text-heading">Dictámenes recientes</h4>
        <div className="space-y-2" data-testid="mentors-sessions-list">
          {sessions.map((s) => (
            <button key={s.session_id} type="button" onClick={() => openSession(s.session_id)} data-testid={`mentor-session-${s.session_id}`}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-border-subtle bg-background/50 p-3 text-left transition hover:border-primary/40">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text-heading">{s.topic}</p>
                <p className="truncate text-xs text-text-muted">{s.client_id} · {new Date(s.created_at).toLocaleString('es-PE')}</p>
              </div>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] ${s.status === 'decided' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/30 bg-amber-500/10 text-amber-200'}`}>
                {s.status === 'decided' ? `Plan ${s.chosen_plan}` : 'Pendiente'}
              </span>
            </button>
          ))}
          {!sessions.length && <p className="text-sm text-text-muted" data-testid="mentors-sessions-empty">Aún no hay dictámenes. Consulta al consejo arriba.</p>}
        </div>
      </div>
    </section>
  );
};

export default MentorsCouncilPanel;
